// =============================================================================
// Email Express — serialized, coalescing autosave queue (Task #2489).
//
// The editor's debounced autosave sends the ENTIRE editor snapshot on every
// save. Before this helper, overlapping saves were neither serialized nor
// cancelled: two quick edits (e.g. pick a block image, then edit the headline)
// produced two full-payload PATCHes racing to the DB — whichever landed LAST
// won wholesale, silently reverting the other edit. Out-of-order responses
// were also written straight into the React Query cache, visibly snapping the
// editor back to stale content.
//
// This pure helper enforces:
//  1. SINGLE FLIGHT — at most one save request is ever in flight.
//  2. COALESCING — saves requested while one is in flight collapse into ONE
//     follow-up save.
//  3. FRESHEST SNAPSHOT — the payload is built via `getPayload()` at the
//     moment each save actually FIRES (never captured at request time), so a
//     queued save always persists post-edit state.
//  4. STALENESS TAGGING — `onSaved` receives `isLatest`; a response that is
//     already superseded (a newer save is queued) must not be written into
//     the query cache.
//
// Pure (no React/query imports) so the concurrency semantics are unit-testable
// in the critical-test suite.
// =============================================================================

export type AutosaveQueue = {
  /** Schedule a save of the CURRENT state (coalesces while one is in flight). */
  request(): void;
  /**
   * Persist the current state NOW and resolve once the queue has fully
   * drained (every requested save settled). Rejects with the save error if
   * the drain ends in a failure — callers (polish/send) must not proceed on
   * an unsaved row.
   */
  flush(): Promise<void>;
  /** True while a save request is in flight. */
  isInFlight(): boolean;
};

export function createAutosaveQueue<P, R>(opts: {
  /** Build the payload from LIVE state. Called when a save fires, never earlier. */
  getPayload: () => P;
  /** Perform the save (PATCH). */
  save: (payload: P) => Promise<R>;
  /**
   * A save settled successfully. `isLatest` is false when a newer save is
   * already queued — treat the response as stale (do NOT write it into any
   * cache); the follow-up save's response supersedes it.
   */
  onSaved: (result: R, payload: P, isLatest: boolean) => void;
  /** A save failed. Queued follow-ups are dropped (the next edit re-kicks). */
  onError: (error: unknown) => void;
}): AutosaveQueue {
  let inFlight = false;
  let queued = false;
  let waiters: Array<{ resolve: () => void; reject: (e: unknown) => void }> = [];

  const settleWaiters = (err?: unknown) => {
    const list = waiters;
    waiters = [];
    for (const w of list) (err === undefined ? w.resolve() : w.reject(err));
  };

  const run = () => {
    inFlight = true;
    queued = false;
    const payload = opts.getPayload();
    opts.save(payload).then(
      (result) => {
        inFlight = false;
        const isLatest = !queued;
        try {
          opts.onSaved(result, payload, isLatest);
        } finally {
          if (queued) run();
          else settleWaiters();
        }
      },
      (error) => {
        inFlight = false;
        // Drop the queued follow-up: retrying immediately after a failure
        // would loop on the same error. The next edit (or flush) re-kicks.
        queued = false;
        try {
          opts.onError(error);
        } finally {
          settleWaiters(error);
        }
      },
    );
  };

  return {
    request() {
      if (inFlight) queued = true;
      else run();
    },
    flush() {
      // Always ensure at least one save fires from the state as of NOW
      // (mirrors the pre-queue callers, which always PATCHed before polish /
      // capture — the server's no-op diffing keeps redundant saves cheap).
      if (inFlight) queued = true;
      else run();
      return new Promise<void>((resolve, reject) => {
        waiters.push({ resolve, reject });
      });
    },
    isInFlight: () => inFlight,
  };
}
